import React, { useState } from "react";
import { Fade, Stack } from "@mui/material";
import { SegmentSizeTooltip, SegmentSubTitle } from "../SegmentSettings.styled";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SegmentTitleTextField } from "../../ContentBlocksEditor/children/ContentBlocksHeader/ContentBlocksHeader.styled";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedSegment } from "../../../redux/selectors/segmentBuilderSelectors";
import {
  handleMenuChange,
  pushToHistoryAction,
  setDesignTabTarget,
  updateSegmentValue,
} from "../../../redux/reducers/segmentBuilder";
import { MIN_SPAN_PCT } from "../../SegmentBuilder/resize/segmentResizeMath";

// port-0921-editwheel-08-segmentsize-zero-clamp (ported from dev PR #4516,
// commit 28e8f6fdf1). Segment Size is a PERCENTAGE of the wheel: the app's
// canonical floor is MIN_SPAN_PCT (1) and the ceiling is 100.
// clampSegmentSize is the single validator for the typed field.
export const MAX_SEGMENT_SIZE = 100;
export const clampSegmentSize = (raw) => {
  // Empty / whitespace: no commit, so the field can be cleared and retyped
  // (never commit a 0-width / empty size).
  if (typeof raw === "string" && raw.trim() === "") return null;
  const num = Number(raw);
  if (!Number.isFinite(num)) return null; // non-numeric -> no commit
  // Floor at MIN_SPAN_PCT (a 0 collapses the segment to an unclickable sliver);
  // ceiling 100 (percentage).
  return Math.min(MAX_SEGMENT_SIZE, Math.max(MIN_SPAN_PCT, num));
};

const SegmentSize = () => {
  const dispatch = useDispatch();
  const selectedSegment = useSelector(getSelectedSegment);

  const handleChange = (event) => {
    // port-0921-editwheel-08-segmentsize-zero-clamp: the old guard
    // (`newValue <= 100` on the raw string) accepted 0 -- collapsing the
    // segment to a 0-width, unclickable sliver that still held its content.
    // clampSegmentSize rejects an empty/non-numeric entry (no commit) and
    // clamps everything else into [MIN_SPAN_PCT..100].
    const clamped = clampSegmentSize(event.target.value);
    if (clamped === null) return;
    dispatch(pushToHistoryAction()); // Push the new state to history

    dispatch(updateSegmentValue({ id: selectedSegment?.id, value: clamped }));
  };

  const handleSegmentSizeClick = () => {
    dispatch(handleMenuChange({ num: 1 })); // Open design tab
    dispatch(setDesignTabTarget("segments")); // Set target to segments tab
  };

  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
      onClick={handleSegmentSizeClick}
      sx={{
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: "#EFE9FE",
        },
      }}
    >
      <Stack direction={"row"} alignItems={"center"} gap={"15px"}>
        <SegmentSubTitle>Segment Size</SegmentSubTitle>
        <SegmentTitleTextField
          // autoFocus
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          sx={{ width: "56px" }}
          value={selectedSegment?.value}
          name="title"
          onChange={handleChange}
          fullWidth
          fontWeightValue="500"
          fontSizeValue="12px"
          backgroundColor="#F6F7F9"
        />
      </Stack>

      <SegmentSizeTooltip
        title="Adjust the size of the Segment. Default size is 1"
        placement="left"
        arrow
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 600 }}
      >
        <InfoOutlinedIcon sx={{ color: "#123051", cursor: "pointer" }} />
      </SegmentSizeTooltip>
    </Stack>
  );
};

export default SegmentSize;
